import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const SVG_HEIGHT = 680;
const PANEL_COUNT = 3;
const MARGIN = { top: 25, right: 30, bottom: 45, left: 65 };
const PANEL_GAP = 16;

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Init. Visit',
  mosquitoTickControl: 'Mosquito/Tick',
};

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  diagnosticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginTop: '16px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  diagItem: {
    textAlign: 'center',
  },
  diagLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    marginBottom: '2px',
  },
  diagValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  diagStatus: {
    fontSize: '10px',
    fontWeight: 500,
    marginTop: '1px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// Normal quantile function (inverse CDF) approximation for Q-Q plot
function normalQuantile(p) {
  if (p <= 0) return -4;
  if (p >= 1) return 4;
  // Rational approximation (Abramowitz and Stegun)
  const a = [
    -3.969683028665376e+01, 2.209460984245205e+02,
    -2.759285104469687e+02, 1.383577518672690e+02,
    -3.066479806614716e+01, 2.506628277459239e+00,
  ];
  const b = [
    -5.447609879822406e+01, 1.615858368580409e+02,
    -1.556989798598866e+02, 6.680131188771972e+01,
    -1.328068155288572e+01,
  ];
  const c = [
    -7.784894002430293e-03, -3.223964580411365e-01,
    -2.400758277161838e+00, -2.549732539343734e+00,
    4.374664141464968e+00, 2.938163982698783e+00,
  ];
  const d = [
    7.784695709041462e-03, 3.224671290700398e-01,
    2.445134137142996e+00, 3.754408661907416e+00,
  ];

  const pLow = 0.02425;
  const pHigh = 1 - pLow;
  let q, r;

  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
           ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  } else if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q /
           (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
            ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  }
}

export default function ResidualsPlot({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const chartData = useMemo(() => {
    if (!data || !data.dates || data.dates.length === 0) return null;
    const n = data.dates.length;
    const points = [];
    for (let i = 0; i < n; i++) {
      points.push({
        date: new Date(data.dates[i]),
        dateStr: data.dates[i],
        residual: data.residuals ? data.residuals[i] : null,
        standardized: data.standardized ? data.standardized[i] : null,
        fitted: data.fitted ? data.fitted[i] : null,
        observed: data.observed ? data.observed[i] : null,
      });
    }

    // Build Q-Q points from standardized residuals
    const stdValues = points
      .map((p) => p.standardized)
      .filter((v) => v != null)
      .sort((a, b) => a - b);

    const qqPoints = stdValues.map((val, i) => {
      const p = (i + 0.5) / stdValues.length;
      return {
        theoretical: normalQuantile(p),
        observed: val,
      };
    });

    return { points, qqPoints };
  }, [data]);

  useEffect(() => {
    if (!chartData || chartData.points.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);

    svg
      .attr('viewBox', `0 0 ${width} ${SVG_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', SVG_HEIGHT)
      .style('overflow', 'visible');

    const innerW = width - MARGIN.left - MARGIN.right;
    const totalPanelSpace = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
    // Give first two panels equal space, Q-Q plot gets less
    const qqRatio = 0.28;
    const mainRatio = (1 - qqRatio) / 2;
    const panelHeights = [
      (totalPanelSpace - PANEL_GAP * (PANEL_COUNT - 1)) * mainRatio,
      (totalPanelSpace - PANEL_GAP * (PANEL_COUNT - 1)) * mainRatio,
      (totalPanelSpace - PANEL_GAP * (PANEL_COUNT - 1)) * qqRatio,
    ];

    const points = chartData.points;
    const qqPoints = chartData.qqPoints;
    const n = points.length;
    const stdDev = data.diagnostics ? data.diagnostics.stdResidual : 1;

    // ═══════════════════════════════════════════════════════════
    // Panel 1: Residuals vs Time
    // ═══════════════════════════════════════════════════════════
    const p1Y = MARGIN.top;
    const p1H = panelHeights[0];
    const g1 = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${p1Y})`);

    const parsedDates = points.map((d) => d.date);
    const x1 = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    const residuals = points.map((d) => d.residual).filter((v) => v != null);
    const rExtent = d3.extent(residuals);
    const rPad = (rExtent[1] - rExtent[0]) * 0.15 || 1;
    const y1 = d3.scaleLinear()
      .domain([rExtent[0] - rPad, rExtent[1] + rPad])
      .nice()
      .range([p1H, 0]);

    // Panel background border
    g1.append('rect')
      .attr('x', 0).attr('y', 0)
      .attr('width', innerW).attr('height', p1H)
      .attr('fill', 'none')
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Horizontal grid lines
    g1.selectAll('.grid-h')
      .data(y1.ticks(6))
      .enter()
      .append('line')
      .attr('x1', 0).attr('x2', innerW)
      .attr('y1', (d) => y1(d)).attr('y2', (d) => y1(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Standard deviation bands (+/-1 and +/-2)
    const bandColors = [
      { lo: -2 * stdDev, hi: 2 * stdDev, fill: '#fef2f2', opacity: 0.4 },
      { lo: -1 * stdDev, hi: 1 * stdDev, fill: '#eff6ff', opacity: 0.5 },
    ];
    bandColors.forEach((band) => {
      const bandTop = Math.min(y1(band.hi), p1H);
      const bandBottom = Math.max(y1(band.lo), 0);
      if (bandBottom > bandTop) {
        g1.append('rect')
          .attr('x', 0).attr('y', bandTop)
          .attr('width', innerW).attr('height', bandBottom - bandTop)
          .attr('fill', band.fill)
          .attr('opacity', band.opacity);
      }
    });

    // +/-1 SD lines
    [-1, 1].forEach((mult) => {
      const val = mult * stdDev;
      if (val >= y1.domain()[0] && val <= y1.domain()[1]) {
        g1.append('line')
          .attr('x1', 0).attr('x2', innerW)
          .attr('y1', y1(val)).attr('y2', y1(val))
          .attr('stroke', '#93c5fd')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }
    });

    // +/-2 SD lines
    [-2, 2].forEach((mult) => {
      const val = mult * stdDev;
      if (val >= y1.domain()[0] && val <= y1.domain()[1]) {
        g1.append('line')
          .attr('x1', 0).attr('x2', innerW)
          .attr('y1', y1(val)).attr('y2', y1(val))
          .attr('stroke', '#fca5a5')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }
    });

    // SD labels
    [
      { val: stdDev, label: '+1\u03C3' },
      { val: -stdDev, label: '-1\u03C3' },
      { val: 2 * stdDev, label: '+2\u03C3' },
      { val: -2 * stdDev, label: '-2\u03C3' },
    ].forEach((item) => {
      if (item.val >= y1.domain()[0] && item.val <= y1.domain()[1]) {
        g1.append('text')
          .attr('x', innerW - 4)
          .attr('y', y1(item.val) - 3)
          .attr('text-anchor', 'end')
          .attr('font-size', '9px')
          .attr('fill', Math.abs(item.val) > 1.5 * stdDev ? '#ef4444' : '#93c5fd')
          .text(item.label);
      }
    });

    // Zero line
    if (y1.domain()[0] <= 0 && y1.domain()[1] >= 0) {
      g1.append('line')
        .attr('x1', 0).attr('x2', innerW)
        .attr('y1', y1(0)).attr('y2', y1(0))
        .attr('stroke', '#374151')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');
    }

    // Scatter dots
    points.forEach((d) => {
      if (d.residual == null || d.standardized == null) return;
      const isOutlier = Math.abs(d.standardized) > 2;
      g1.append('circle')
        .attr('cx', x1(d.date))
        .attr('cy', y1(d.residual))
        .attr('r', isOutlier ? 5 : 4)
        .attr('fill', isOutlier ? '#dc2626' : '#2563eb')
        .attr('opacity', isOutlier ? 1.0 : 0.7)
        .attr('stroke', isOutlier ? '#fff' : 'none')
        .attr('stroke-width', isOutlier ? 1.5 : 0)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('r', 7);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            panel: 'time',
            date: d.dateStr,
            residual: d.residual,
            standardized: d.standardized,
            fitted: d.fitted,
            observed: d.observed,
            isOutlier,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('r', isOutlier ? 5 : 4);
          setTooltip(null);
        });
    });

    // Y axis
    g1.append('g')
      .call(
        d3.axisLeft(y1)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => v.toFixed(1))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g1.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -p1H / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Residual');

    // X axis
    g1.append('g')
      .attr('transform', `translate(0, ${p1H})`)
      .call(
        d3.axisBottom(x1)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Panel title
    g1.append('text')
      .attr('x', 0)
      .attr('y', -8)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Residuals vs Time');

    // ═══════════════════════════════════════════════════════════
    // Panel 2: Residuals vs Fitted
    // ═══════════════════════════════════════════════════════════
    const p2Y = p1Y + p1H + PANEL_GAP;
    const p2H = panelHeights[1];
    const g2 = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${p2Y})`);

    const fittedValues = points.map((d) => d.fitted).filter((v) => v != null);
    const fExtent = d3.extent(fittedValues);
    const fPad = (fExtent[1] - fExtent[0]) * 0.1 || 1;

    const x2 = d3.scaleLinear()
      .domain([fExtent[0] - fPad, fExtent[1] + fPad])
      .nice()
      .range([0, innerW]);

    const y2 = d3.scaleLinear()
      .domain([rExtent[0] - rPad, rExtent[1] + rPad])
      .nice()
      .range([p2H, 0]);

    // Panel background border
    g2.append('rect')
      .attr('x', 0).attr('y', 0)
      .attr('width', innerW).attr('height', p2H)
      .attr('fill', 'none')
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Grid lines
    g2.selectAll('.grid-h')
      .data(y2.ticks(6))
      .enter()
      .append('line')
      .attr('x1', 0).attr('x2', innerW)
      .attr('y1', (d) => y2(d)).attr('y2', (d) => y2(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // SD bands
    bandColors.forEach((band) => {
      const bandTop = Math.min(y2(band.hi), p2H);
      const bandBottom = Math.max(y2(band.lo), 0);
      if (bandBottom > bandTop) {
        g2.append('rect')
          .attr('x', 0).attr('y', bandTop)
          .attr('width', innerW).attr('height', bandBottom - bandTop)
          .attr('fill', band.fill)
          .attr('opacity', band.opacity);
      }
    });

    // SD lines
    [-1, 1].forEach((mult) => {
      const val = mult * stdDev;
      if (val >= y2.domain()[0] && val <= y2.domain()[1]) {
        g2.append('line')
          .attr('x1', 0).attr('x2', innerW)
          .attr('y1', y2(val)).attr('y2', y2(val))
          .attr('stroke', '#93c5fd')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }
    });
    [-2, 2].forEach((mult) => {
      const val = mult * stdDev;
      if (val >= y2.domain()[0] && val <= y2.domain()[1]) {
        g2.append('line')
          .attr('x1', 0).attr('x2', innerW)
          .attr('y1', y2(val)).attr('y2', y2(val))
          .attr('stroke', '#fca5a5')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }
    });

    // Zero line
    if (y2.domain()[0] <= 0 && y2.domain()[1] >= 0) {
      g2.append('line')
        .attr('x1', 0).attr('x2', innerW)
        .attr('y1', y2(0)).attr('y2', y2(0))
        .attr('stroke', '#374151')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');
    }

    // Scatter dots
    points.forEach((d) => {
      if (d.residual == null || d.fitted == null || d.standardized == null) return;
      const isOutlier = Math.abs(d.standardized) > 2;
      g2.append('circle')
        .attr('cx', x2(d.fitted))
        .attr('cy', y2(d.residual))
        .attr('r', isOutlier ? 5 : 4)
        .attr('fill', isOutlier ? '#dc2626' : '#8b5cf6')
        .attr('opacity', isOutlier ? 1.0 : 0.7)
        .attr('stroke', isOutlier ? '#fff' : 'none')
        .attr('stroke-width', isOutlier ? 1.5 : 0)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('r', 7);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            panel: 'fitted',
            date: d.dateStr,
            residual: d.residual,
            standardized: d.standardized,
            fitted: d.fitted,
            observed: d.observed,
            isOutlier,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('r', isOutlier ? 5 : 4);
          setTooltip(null);
        });
    });

    // X axis
    g2.append('g')
      .attr('transform', `translate(0, ${p2H})`)
      .call(
        d3.axisBottom(x2)
          .ticks(8)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(0)}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g2.append('text')
      .attr('x', innerW / 2)
      .attr('y', p2H + 35)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Fitted Value ($)');

    // Y axis
    g2.append('g')
      .call(
        d3.axisLeft(y2)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => v.toFixed(1))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g2.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -p2H / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Residual');

    // Panel title
    g2.append('text')
      .attr('x', 0)
      .attr('y', -8)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Residuals vs Fitted');

    // Heteroscedasticity warning
    if (data.diagnostics && data.diagnostics.heteroscedastic) {
      g2.append('text')
        .attr('x', innerW - 4)
        .attr('y', -8)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#dc2626')
        .text('Heteroscedastic pattern detected');
    }

    // ═══════════════════════════════════════════════════════════
    // Panel 3: Q-Q Plot (small version)
    // ═══════════════════════════════════════════════════════════
    const p3Y = p2Y + p2H + PANEL_GAP;
    const p3H = panelHeights[2];
    const g3 = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${p3Y})`);

    // Use a square Q-Q plot area centered in the panel
    const qqSize = Math.min(p3H, innerW * 0.4);
    const qqOffsetX = (innerW - qqSize) / 2;
    const gQQ = g3.append('g').attr('transform', `translate(${qqOffsetX}, 0)`);

    // Panel background border
    gQQ.append('rect')
      .attr('x', 0).attr('y', 0)
      .attr('width', qqSize).attr('height', p3H)
      .attr('fill', 'none')
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    if (qqPoints.length > 0) {
      const qqXExtent = d3.extent(qqPoints, (d) => d.theoretical);
      const qqYExtent = d3.extent(qqPoints, (d) => d.observed);
      const qqXPad = (qqXExtent[1] - qqXExtent[0]) * 0.1 || 0.5;
      const qqYPad = (qqYExtent[1] - qqYExtent[0]) * 0.1 || 0.5;

      const xQQ = d3.scaleLinear()
        .domain([qqXExtent[0] - qqXPad, qqXExtent[1] + qqXPad])
        .nice()
        .range([0, qqSize]);

      const yQQ = d3.scaleLinear()
        .domain([qqYExtent[0] - qqYPad, qqYExtent[1] + qqYPad])
        .nice()
        .range([p3H, 0]);

      // Grid lines
      gQQ.selectAll('.grid-h')
        .data(yQQ.ticks(4))
        .enter()
        .append('line')
        .attr('x1', 0).attr('x2', qqSize)
        .attr('y1', (d) => yQQ(d)).attr('y2', (d) => yQQ(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      gQQ.selectAll('.grid-v')
        .data(xQQ.ticks(4))
        .enter()
        .append('line')
        .attr('x1', (d) => xQQ(d)).attr('x2', (d) => xQQ(d))
        .attr('y1', 0).attr('y2', p3H)
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Reference diagonal (y = x for standardized residuals)
      const refDomain = xQQ.domain();
      gQQ.append('line')
        .attr('x1', xQQ(refDomain[0]))
        .attr('y1', yQQ(refDomain[0]))
        .attr('x2', xQQ(refDomain[1]))
        .attr('y2', yQQ(refDomain[1]))
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');

      // Scatter points
      qqPoints.forEach((pt) => {
        const isOutlier = Math.abs(pt.observed) > 2;
        gQQ.append('circle')
          .attr('cx', xQQ(pt.theoretical))
          .attr('cy', yQQ(pt.observed))
          .attr('r', isOutlier ? 4 : 3)
          .attr('fill', isOutlier ? '#dc2626' : '#16a34a')
          .attr('opacity', isOutlier ? 1.0 : 0.7)
          .attr('stroke', isOutlier ? '#fff' : 'none')
          .attr('stroke-width', isOutlier ? 1 : 0)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            d3.select(event.currentTarget).attr('r', 6);
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              panel: 'qq',
              theoretical: pt.theoretical,
              observed: pt.observed,
              isOutlier,
            });
          })
          .on('mouseleave', (event) => {
            d3.select(event.currentTarget).attr('r', isOutlier ? 4 : 3);
            setTooltip(null);
          });
      });

      // X axis
      gQQ.append('g')
        .attr('transform', `translate(0, ${p3H})`)
        .call(
          d3.axisBottom(xQQ)
            .ticks(5)
            .tickSize(0)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280');

      // X axis label
      gQQ.append('text')
        .attr('x', qqSize / 2)
        .attr('y', p3H + 30)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text('Theoretical Quantiles');

      // Y axis
      gQQ.append('g')
        .call(
          d3.axisLeft(yQQ)
            .ticks(4)
            .tickSize(0)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280');
    }

    // Panel title
    g3.append('text')
      .attr('x', 0)
      .attr('y', -8)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Q-Q Plot (Standardized Residuals)');

    // Normal reference label
    g3.append('text')
      .attr('x', innerW - 4)
      .attr('y', -8)
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('Normal reference line');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [chartData, data]);

  // Handle window resize
  useEffect(() => {
    if (!chartData || chartData.points.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [chartData]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Regression Residuals Analysis</div>
        <div style={styles.subtitle}>Diagnostic plots for price trend model fit</div>
        <div style={styles.emptyState}>No residuals data available</div>
      </div>
    );
  }

  const diag = data.diagnostics || {};
  const fieldLabel = FIELD_LABELS[data.field] || data.field || 'Price';
  const outlierCount = (data.standardized || []).filter((v) => v != null && Math.abs(v) > 2).length;

  // Diagnostics pass/fail logic
  const diagChecks = [
    {
      label: 'Mean Residual',
      value: diag.meanResidual != null ? diag.meanResidual.toFixed(3) : '--',
      pass: diag.meanResidual != null ? Math.abs(diag.meanResidual) < 0.1 : null,
      hint: 'Should be ~0',
    },
    {
      label: 'Std Residual',
      value: diag.stdResidual != null ? diag.stdResidual.toFixed(3) : '--',
      pass: null, // informational
      hint: 'Scale of residuals',
    },
    {
      label: 'Skewness',
      value: diag.skewness != null ? diag.skewness.toFixed(3) : '--',
      pass: diag.skewness != null ? Math.abs(diag.skewness) < 1 : null,
      hint: '|skew| < 1 is good',
    },
    {
      label: 'Kurtosis',
      value: diag.kurtosis != null ? diag.kurtosis.toFixed(2) : '--',
      pass: diag.kurtosis != null ? Math.abs(diag.kurtosis - 3) < 2 : null,
      hint: '~3 for normal',
    },
    {
      label: 'Durbin-Watson',
      value: diag.durbinWatson != null ? diag.durbinWatson.toFixed(2) : '--',
      pass: diag.durbinWatson != null ? diag.durbinWatson > 1.5 && diag.durbinWatson < 2.5 : null,
      hint: '~2 = no autocorrelation',
    },
    {
      label: 'Shapiro-Wilk',
      value: diag.shapiroWilk != null ? diag.shapiroWilk.toFixed(3) : '--',
      pass: diag.shapiroWilk != null ? diag.shapiroWilk > 0.95 : null,
      hint: '>0.95 = normal',
    },
  ];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Regression Residuals Analysis</div>
        </div>
        {outlierCount > 0 && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 10px',
            borderRadius: '999px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            fontSize: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {outlierCount} outlier{outlierCount !== 1 ? 's' : ''} (|z|&gt;2)
          </div>
        )}
      </div>
      <div style={styles.subtitle}>
        Diagnostic plots for price trend model fit
        {fieldLabel !== 'Price' ? ` \u2014 ${fieldLabel}` : ''}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="10" height="10">
            <circle cx="5" cy="5" r="4" fill="#2563eb" opacity="0.7" />
          </svg>
          <span>Normal (vs time)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="10" height="10">
            <circle cx="5" cy="5" r="4" fill="#8b5cf6" opacity="0.7" />
          </svg>
          <span>Normal (vs fitted)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="10" height="10">
            <circle cx="5" cy="5" r="4" fill="#16a34a" opacity="0.7" />
          </svg>
          <span>Q-Q point</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="10" height="10">
            <circle cx="5" cy="5" r="4" fill="#dc2626" stroke="#fff" strokeWidth="1" />
          </svg>
          <span>Outlier (|z|&gt;2)</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Diagnostics panel */}
      <div style={styles.diagnosticsGrid}>
        {diagChecks.map((check) => (
          <div key={check.label} style={styles.diagItem}>
            <div style={styles.diagLabel}>{check.label}</div>
            <div style={{
              ...styles.diagValue,
              color: check.pass === false ? '#dc2626' : check.pass === true ? '#16a34a' : '#1f2937',
            }}>
              {check.value}
            </div>
            <div style={{
              ...styles.diagStatus,
              color: check.pass === false ? '#dc2626' : check.pass === true ? '#16a34a' : '#9ca3af',
            }}>
              {check.pass === true ? 'Pass' : check.pass === false ? 'Fail' : check.hint}
            </div>
          </div>
        ))}
      </div>

      {/* Heteroscedasticity row */}
      {diag.heteroscedastic != null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '8px',
          padding: '6px 12px',
          background: diag.heteroscedastic ? '#fef2f2' : '#f0fdf4',
          borderRadius: '6px',
          border: `1px solid ${diag.heteroscedastic ? '#fecaca' : '#bbf7d0'}`,
          fontSize: '12px',
        }}>
          <span style={{
            fontWeight: 600,
            color: diag.heteroscedastic ? '#dc2626' : '#16a34a',
          }}>
            {diag.heteroscedastic ? 'Fail' : 'Pass'}
          </span>
          <span style={{ color: '#374151' }}>
            Heteroscedasticity: {diag.heteroscedastic
              ? 'Variance changes over fitted values -- model may need transformation'
              : 'Constant variance across fitted values'}
          </span>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.panel === 'qq' ? (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                Q-Q Point
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Theoretical</span>
                <span style={{ fontWeight: 600 }}>{tooltip.theoretical.toFixed(3)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Observed</span>
                <span style={{ fontWeight: 600 }}>{tooltip.observed.toFixed(3)}</span>
              </div>
              {tooltip.isOutlier && (
                <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>
                  Outlier
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                {formatDateFull(tooltip.date)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Observed</span>
                <span style={{ fontWeight: 600 }}>
                  {tooltip.observed != null ? `$${tooltip.observed.toFixed(2)}` : 'N/A'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Fitted</span>
                <span style={{ fontWeight: 600, color: '#8b5cf6' }}>
                  {tooltip.fitted != null ? `$${tooltip.fitted.toFixed(2)}` : 'N/A'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Residual</span>
                <span style={{ fontWeight: 600, color: tooltip.residual >= 0 ? '#16a34a' : '#dc2626' }}>
                  {tooltip.residual != null
                    ? `${tooltip.residual >= 0 ? '+' : ''}${tooltip.residual.toFixed(3)}`
                    : 'N/A'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Z-Score</span>
                <span style={{ fontWeight: 600 }}>
                  {tooltip.standardized != null ? tooltip.standardized.toFixed(2) : 'N/A'}
                </span>
              </div>
              {tooltip.isOutlier && (
                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f3f4f6', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>
                  Outlier (|z| &gt; 2)
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
